import { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { readCheckoutIntent, clearCheckoutIntent } from "../lib/checkoutIntent";
import { startCheckout } from "../lib/startCheckout";

/**
 * CheckoutResume — resumes a pending checkout once the user is authenticated.
 *
 * Mounted once at the app root (inside AuthProvider). When `user` becomes truthy
 * and a saved checkout intent exists, it launches checkout automatically. This is
 * the single hook that covers every auth path:
 *   - password login / signup: stay on-site, `user` updates → effect fires.
 *   - OAuth / SSO: a fresh page load → AuthContext.refresh() sets `user` on mount
 *     → effect fires (intent survived in localStorage across the external redirect).
 *
 * Because it redirects off-site to Polar, it runs before the onboarding gate —
 * intent users go straight to checkout and onboard when they return.
 */
export default function CheckoutResume() {
  const { user } = useAuth();
  const fired = useRef(false);

  useEffect(() => {
    if (!user || fired.current) return;
    const intent = readCheckoutIntent();
    if (!intent) return;
    fired.current = true;
    clearCheckoutIntent();
    startCheckout({ plan: intent.plan, billing: intent.billing, promo: intent.promo }).catch(() => {
      // If the launch fails (e.g. Polar hiccup), the user lands in the app and can
      // retry from /pricing — we don't block them here.
    });
  }, [user]);

  return null;
}
